import type { ErrorEvent } from '@sentry/browser';
import type { ErrorEvent as ReactErrorEvent } from '@sentry/react';
import supportedBrowsers from '../../../../../supportedBrowsers.js';

export default class SentryErrorFilter {
    readonly #noisyMessages: RegExp[] = [
        /Java object is gone/i,
        /fbq is not defined/i,
        /Load failed/i,
        /Fetch is aborted/i,
        /Failed to fetch/i,
        /NetworkError when attempting to fetch resource./i,
        /ChunkLoadError/i,
        /Cannot assign to read only property.*/i,
        /Loading chunk .* failed/i,
        /Event `CustomEvent` \(type=unhandledrejection\) captured as promise rejection/i
    ];

    readonly #noisyUrls: RegExp[] = [
        /pinterest\.com/,
        /pinimg\.com/,
        /criteo\.com/,
        /bing\.com/,
        /js-agent\.newrelic\.com/,
        /\/metrics\//
    ];

    readonly #noisyRequestUrls: RegExp[] = [
        /pinterest\.com/,
        /google-analytics\.com/,
        /pinimg\.com/,
        /measurement-api.criteo\.com/
    ];

    readonly #ignoredPackages: string[] = [
        'node_modules/@sentry',
        'node_modules/mapbox-gl-leaflet'
    ];

    #isSupportedBrowser(browser: string): boolean {
        return supportedBrowsers.test(browser);
    }

    /**
     * Checks if any stack trace frame comes from known noisy third-party script URLs.
     * Focuses on the script origin, not the network destination.
     */
    #isNoisyUrl(stackFrames?: Array<{ filename?: string }>): boolean {
        return (
            stackFrames?.some((frame) =>
                this.#noisyUrls.some((pattern) => pattern.test(frame?.filename || ''))
            ) || false
        );
    }

    /**
     * Checks if the request URL involved in the error points to a noisy third-party domain.
     * Focuses on the HTTP destination, not the script file origin.
     */
    #isNoisyRequestUrl(url?: string): boolean {
        if (!url) return false;
        return this.#noisyRequestUrls.some((pattern) => pattern.test(url));
    }

    #isNoisyMessage(message: string): boolean {
        return this.#noisyMessages.some((regex) => regex.test(message));
    }

    #hasOurDomainInStack(stackFrames?: Array<{ filename?: string }>): boolean | undefined {
        return stackFrames?.some((frame): boolean => {
            const filename = frame?.filename;

            if (
                !filename ||
                this.#ignoredPackages.some((ignoredPackage): boolean =>
                    filename.includes(ignoredPackage)
                )
            ) {
                return false;
            }

            return [
                'natuurhuisje.nl',
                'natuurhuisje.be',
                'nature.house',
                'naturhaeuschen.de',
                'casanellanatura.it',
                'maisonnature.fr',
                'maisonnature.be'
            ].some((domain) => filename.includes(domain));
        });
    }

    public shouldIgnore(event: ErrorEvent | ReactErrorEvent): boolean {
        if (!this.#isSupportedBrowser(navigator.userAgent)) {
            return true;
        }

        const frames = event.exception?.values?.[0]?.stacktrace?.frames;
        const requestUrl = event.request?.url;
        const errorMessage = event.exception?.values?.[0]?.value || '';

        const isNoisyByUrl = this.#isNoisyUrl(frames);
        const isNoisyByMessage = this.#isNoisyMessage(errorMessage);
        const isNoisyByRequestUrl = this.#isNoisyRequestUrl(requestUrl);

        return (
            (isNoisyByUrl || isNoisyByMessage || isNoisyByRequestUrl) &&
            !this.#hasOurDomainInStack(frames)
        );
    }
}
